import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Plus, Home as HomeIcon, Wallet, TrendingUp, Calendar as CalIcon, Save, Check, X, Send, Pencil,
  Star, Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { format, isSameDay } from "date-fns";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip } from "recharts";
import Eyebrow from "@/components/common/Eyebrow";
import VillaWizard from "@/components/villa/VillaWizard";

const CARD_SX = { borderRadius: 4, border: 0, boxShadow: 1 };

const BOOKING_STATUS_COLORS = {
  confirmed: { color: "#3b82f6", bg: "rgba(59,130,246,0.12)" },
  approved: { color: "#16a34a", bg: "rgba(34,197,94,0.12)" },
  declined: { color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
  cancelled: { color: "#6b7280", bg: "rgba(107,114,128,0.12)" },
};

const VILLA_STATUS_COLORS = {
  pending: { color: "#d97706", bg: "rgba(217,119,6,0.12)" },
  approved: { color: "#16a34a", bg: "rgba(34,197,94,0.12)" },
  rejected: { color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
};

function TabPanel({ value, current, children }) {
  if (value !== current) return null;
  return <Box sx={{ pt: 0 }}>{children}</Box>;
}

function StatusChip({ status, map }) {
  const c = map[status] || map.cancelled || Object.values(map)[0];
  return (
    <Chip
      label={status}
      size="small"
      sx={{ color: c.color, bgcolor: c.bg, fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", height: 22 }}
    />
  );
}

function EmptyState({ Icon, label, testId }) {
  return (
    <Box sx={{ py: 8, textAlign: "center" }} data-testid={testId}>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: (t) => alpha(t.palette.text.primary, 0.05), display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
        <Icon size={22} className="text-gold" />
      </Box>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
  );
}

function StatCard({ Icon, value, label, color }) {
  return (
    <Box sx={{ ...CARD_SX, p: 2.5 }}>
      <Box sx={{ width: 38, height: 38, borderRadius: 2.5, bgcolor: alpha(color, 0.12), color, display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5 }}>
        <Icon size={17} />
      </Box>
      <Typography className="font-serif-display" sx={{ fontSize: 24 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.1em", display: "block", mt: 0.5 }}>{label}</Typography>
    </Box>
  );
}

function RevenueTrend({ bookings }) {
  const theme = useTheme();
  const data = useMemo(() => {
    const now = new Date();
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      return { key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleString("en-US", { month: "short" }), net: 0 };
    });
    bookings.forEach((b) => {
      const d = new Date(b.check_in);
      const m = months.find((mo) => mo.key === `${d.getFullYear()}-${d.getMonth()}`);
      if (m) m.net += b.net_payout;
    });
    return months.map(({ label, net }) => ({ label, net }));
  }, [bookings]);

  return (
    <Box sx={{ ...CARD_SX, p: 3 }} data-testid="owner-revenue-trend">
      <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.5 }}>Earnings trend</Typography>
      <Typography variant="caption" color="text.secondary">Net payout by check-in month, last 6 months</Typography>
      <Box sx={{ height: 220, mt: 2 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="ownerRevenueFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#C5A059" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#C5A059" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
            <XAxis dataKey="label" stroke={theme.palette.text.secondary} fontSize={12} tickLine={false} axisLine={false} />
            <YAxis
              stroke={theme.palette.text.secondary} fontSize={12} tickLine={false} axisLine={false}
              domain={[0, (max) => Math.max(max, 5000)]}
              tickFormatter={(v) => `₹${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`}
              width={48}
            />
            <ChartTooltip formatter={(v) => [`₹${Number(v).toLocaleString()}`, "Net payout"]} contentStyle={{ borderRadius: 10, border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", fontSize: 13 }} />
            <Area type="monotone" dataKey="net" stroke="#C5A059" strokeWidth={2} fill="url(#ownerRevenueFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

function BookingsInbox() {
  const [bookings, setBookings] = useState([]);
  const [msgFor, setMsgFor] = useState(null);
  const [msgText, setMsgText] = useState("");
  const load = () => api.get("/owner/bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (b, status) => {
    try { await api.patch(`/owner/bookings/${b.id}/status`, { status }); toast(`Booking ${status}`); load(); }
    catch { toast("Could not update"); }
  };
  const sendMsg = async () => {
    if (!msgText.trim()) return;
    try { await api.post(`/owner/bookings/${msgFor.id}/message`, { message: msgText }); toast("Message sent"); setMsgText(""); setMsgFor(null); }
    catch { toast("Could not send"); }
  };

  if (bookings.length === 0) return <Box sx={CARD_SX}><EmptyState Icon={Inbox} label="No incoming bookings yet." testId="no-owner-bookings" /></Box>;

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {bookings.map((b) => (
          <Box
            key={b.id}
            sx={{ ...CARD_SX, display: "flex", gap: 2, p: 2, transition: "box-shadow 0.2s ease", "&:hover": { boxShadow: 3 } }}
            data-testid={`owner-booking-${b.id}`}
          >
            <Box component="img" src={b.villa_image} alt="" sx={{ width: 96, height: 96, borderRadius: 3, objectFit: "cover", flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography className="font-serif-display" sx={{ fontSize: 18 }}>{b.villa_name}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {format(new Date(b.check_in), "dd MMM")} – {format(new Date(b.check_out), "dd MMM yyyy")} · {b.guests} guests
              </Typography>
              <Box sx={{ mt: 1 }} data-testid={`booking-status-${b.id}`}>
                <StatusChip status={b.status} map={BOOKING_STATUS_COLORS} />
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1, textAlign: "right" }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Payout</Typography>
                <Typography variant="body2" fontWeight={600} className="text-gold">₹{Math.round(b.total * 0.85).toLocaleString()}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.75 }}>
                {b.status === "confirmed" && (
                  <>
                    <Tooltip title="Approve">
                      <IconButton size="small" onClick={() => setStatus(b, "approved")} data-testid={`approve-${b.id}`} sx={{ width: 32, height: 32, border: 1, borderColor: "success.main", color: "success.main" }}><Check size={13} /></IconButton>
                    </Tooltip>
                    <Tooltip title="Decline">
                      <IconButton size="small" onClick={() => setStatus(b, "declined")} data-testid={`decline-${b.id}`} sx={{ width: 32, height: 32, border: 1, borderColor: "error.main", color: "error.main" }}><X size={13} /></IconButton>
                    </Tooltip>
                  </>
                )}
                <Tooltip title="Message guest">
                  <IconButton size="small" onClick={() => setMsgFor(b)} data-testid={`msg-${b.id}`} sx={{ width: 32, height: 32, border: 1, borderColor: "divider" }}><Send size={12} /></IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
      <Dialog open={!!msgFor} onClose={() => setMsgFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle><Typography className="font-serif-display" sx={{ fontSize: 24 }}>Message guest</Typography></DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">Regarding: <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>{msgFor?.villa_name}</Box></Typography>
            <TextField
              multiline rows={4} fullWidth
              value={msgText} onChange={(e) => setMsgText(e.target.value)}
              placeholder="Write your message…"
              inputProps={{ "data-testid": "owner-msg-text" }}
            />
            <Button onClick={sendMsg} variant="contained" fullWidth data-testid="owner-msg-send">Send message</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CalendarBlock({ villa, onUpdate }) {
  const [selected, setSelected] = useState((villa.blocked_dates || []).map((d) => new Date(d)));
  const save = async () => {
    try {
      await api.patch(`/owner/villas/${villa.id}/blocked-dates`, {
        dates: selected.map((d) => d.toISOString().split("T")[0]),
      });
      toast(`Blocked dates updated for ${villa.name}`);
      onUpdate();
    } catch { toast("Could not update"); }
  };
  const toggleDay = (day) => {
    const jsDay = day?.toDate ? day.toDate() : day;
    setSelected((prev) => {
      const exists = prev.some((d) => isSameDay(d, jsDay));
      return exists ? prev.filter((d) => !isSameDay(d, jsDay)) : [...prev, jsDay];
    });
  };

  return (
    <Box sx={{ ...CARD_SX, p: { xs: 1.5, sm: 2.5 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, px: { xs: 1, sm: 0 } }}>
        <Box>
          <Typography className="font-serif-display" sx={{ fontSize: 18 }}>{villa.name}</Typography>
          <Typography variant="caption" color="text.secondary">{selected.length} dates blocked</Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<Save size={12} />} onClick={save} data-testid={`save-blocked-${villa.id}`} sx={{ fontSize: 12 }}>
          Save
        </Button>
      </Box>
      <Box sx={{ overflowX: "auto" }}>
        <DateCalendar
          value={null}
          onChange={(day) => toggleDay(day)}
          slots={{
            day: (props) => {
              const jsDay = props.day?.toDate ? props.day.toDate() : props.day;
              const isSelected = selected.some((d) => isSameDay(d, jsDay));
              return (
                <PickersDay
                  {...props}
                  selected={isSelected}
                  sx={isSelected ? { bgcolor: "primary.main !important", color: "primary.contrastText" } : undefined}
                />
              );
            },
          }}
        />
      </Box>
    </Box>
  );
}

export default function OwnerPortal() {
  const { user, loading: authLoading } = useAuth();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));
  const [villas, setVillas] = useState([]);
  const [payouts, setPayouts] = useState({ bookings: [], total_gross: 0, total_net: 0, count: 0 });
  const [wizardVilla, setWizardVilla] = useState(null); // null = closed, "new" = create, villa object = edit
  const [tab, setTab] = useState("listings");

  const load = () => {
    api.get("/owner/villas").then((r) => setVillas(r.data)).catch(() => {});
    api.get("/owner/payouts").then((r) => setPayouts(r.data)).catch(() => {});
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (authLoading) return <Box sx={{ py: 16, textAlign: "center", fontSize: 14, color: "text.secondary" }} data-testid="owner-loading">Loading…</Box>;
  if (!user) return <Navigate to="/login?redirect=/owner" replace />;
  if (user.role !== "owner" && user.role !== "admin") {
    return (
      <Box sx={{ maxWidth: 480, mx: "auto", py: 12, textAlign: "center", px: 3 }} data-testid="owner-forbidden">
        <Typography className="font-serif-display" sx={{ fontSize: 30, mb: 1.5 }}>Owner access required</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Sign in as an owner to manage your listings.</Typography>
        <Typography variant="caption" color="text.secondary">Demo owner: owner@nirvagoo.com / Owner@123</Typography>
      </Box>
    );
  }

  const upcoming = payouts.bookings.filter((b) => new Date(b.check_in) >= new Date()).length;
  const avgRating = villas.length ? Math.round((villas.reduce((s, v) => s + (v.rating || 0), 0) / villas.length) * 10) / 10 : 0;

  const STATS = [
    { Icon: HomeIcon, value: villas.length, label: "Listings", color: "#0ea5e9" },
    { Icon: CalIcon, value: upcoming, label: "Upcoming", color: "#f59e0b" },
    { Icon: TrendingUp, value: `₹${payouts.total_gross.toLocaleString()}`, label: "Gross revenue", color: "#16a34a" },
    { Icon: Wallet, value: `₹${payouts.total_net.toLocaleString()}`, label: "Net payout", color: "#C5A059" },
    { Icon: Star, value: avgRating || "—", label: "Avg rating", color: "#e11d48" },
  ];

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 3, lg: 6 }, py: 6 }} data-testid="owner-portal">
      <Box
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 3, flexWrap: "wrap",
          mb: 5, p: { xs: 3, md: 4 }, borderRadius: 5,
          background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.12)}, ${alpha(t.palette.primary.main, 0.02)})`,
        }}
      >
        <Box>
          <Eyebrow sx={{ mb: 1 }}>Owner portal</Eyebrow>
          <Typography className="font-serif-display" sx={{ fontSize: { xs: 32, md: 44 } }}>Welcome back, {user.name.split(" ")[0]}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>Manage your listings, bookings and payouts in one place.</Typography>
        </Box>
        <Button variant="contained" size="large" startIcon={<Plus size={16} />} onClick={() => setWizardVilla("new")} data-testid="new-villa-btn">List new villa</Button>
        <Dialog open={!!wizardVilla} onClose={() => setWizardVilla(null)} maxWidth="md" fullWidth fullScreen={fullScreenDialog}>
          <DialogTitle>
            <Typography className="font-serif-display" sx={{ fontSize: 24 }}>
              {wizardVilla === "new" ? "List a new villa" : "Edit villa"}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <VillaWizard
              key={wizardVilla === "new" ? "new" : wizardVilla?.id}
              villa={wizardVilla === "new" ? null : wizardVilla}
              onSaved={() => { setWizardVilla(null); load(); }}
              onCancel={() => setWizardVilla(null)}
            />
          </DialogContent>
        </Dialog>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", lg: "repeat(5, 1fr)" }, gap: 2, mb: 4 }}>
        {STATS.map((s) => <StatCard key={s.label} {...s} />)}
      </Box>

      <Box sx={{ mb: 5 }}>
        <RevenueTrend bookings={payouts.bookings} />
      </Box>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{
          mb: 4, borderBottom: 1, borderColor: "divider",
          "& .MuiTab-root": { textTransform: "none", fontWeight: 500, fontSize: 15, minHeight: 44 },
          "& .Mui-selected": { fontWeight: 600 },
        }}
      >
        <Tab label="Listings" value="listings" data-testid="owner-tab-listings" />
        <Tab label="Bookings" value="bookings" data-testid="owner-tab-bookings" />
        <Tab label="Calendar" value="calendar" data-testid="owner-tab-calendar" />
        <Tab label="Payouts" value="payouts" data-testid="owner-tab-payouts" />
      </Tabs>

      <TabPanel value="listings" current={tab}>
        {villas.length === 0 ? (
          <Box sx={CARD_SX}><EmptyState Icon={HomeIcon} label="You haven't listed a villa yet." testId="no-owner-villas" /></Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "repeat(3, 1fr)" }, gap: 3 }}>
            {villas.map((v) => (
              <Box
                key={v.id}
                component={Link}
                to={`/villas/${v.id}`}
                data-testid={`owner-villa-${v.id}`}
                sx={{
                  ...CARD_SX, overflow: "hidden", textDecoration: "none", color: "inherit", display: "block",
                  transition: "box-shadow 0.2s ease, transform 0.2s ease",
                  "&:hover": { boxShadow: 4, transform: "translateY(-2px)" },
                }}
              >
                <Box sx={{ aspectRatio: "4 / 3", overflow: "hidden", position: "relative" }}>
                  <Box component="img" src={v.images[0]} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <Box sx={{ position: "absolute", top: 8, left: 8 }}>
                    <StatusChip status={v.status || "approved"} map={VILLA_STATUS_COLORS} />
                  </Box>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setWizardVilla(v); }}
                      data-testid={`edit-owner-villa-${v.id}`}
                      sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(0,0,0,0.55)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
                    >
                      <Pencil size={13} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                    <Typography className="font-serif-display" sx={{ fontSize: 18 }}>{v.name}</Typography>
                    {v.rating > 0 && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flexShrink: 0, mt: 0.5 }}>
                        <Star size={12} fill="#C5A059" color="#C5A059" />
                        <Typography variant="caption" fontWeight={600}>{v.rating}</Typography>
                      </Box>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">{v.location}</Typography>
                  <Box sx={{ mt: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" fontWeight={500}>₹{v.price_per_night.toLocaleString()}/night</Typography>
                    <Typography variant="caption" className="text-gold" sx={{ textTransform: "uppercase", letterSpacing: "0.1em" }}>{v.blocked_dates?.length || 0} blocked</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </TabPanel>

      <TabPanel value="bookings" current={tab}>
        <BookingsInbox />
      </TabPanel>

      <TabPanel value="calendar" current={tab}>
        {villas.length === 0 ? (
          <Box sx={CARD_SX}><EmptyState Icon={CalIcon} label="List a villa to manage its calendar." testId="no-owner-calendar" /></Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
            {villas.map((v) => <CalendarBlock key={v.id} villa={v} onUpdate={load} />)}
          </Box>
        )}
      </TabPanel>

      <TabPanel value="payouts" current={tab}>
        <Box sx={{ ...CARD_SX, overflowX: "auto", overflowY: "hidden" }}>
          <Table size="small" sx={{ minWidth: 480 }}>
            <TableHead sx={{ bgcolor: (t) => alpha(t.palette.text.primary, 0.03) }}>
              <TableRow>
                <TableCell><Eyebrow sx={{ fontSize: 10 }}>Villa</Eyebrow></TableCell>
                <TableCell><Eyebrow sx={{ fontSize: 10 }}>Dates</Eyebrow></TableCell>
                <TableCell align="right"><Eyebrow sx={{ fontSize: 10 }}>Gross</Eyebrow></TableCell>
                <TableCell align="right"><Eyebrow sx={{ fontSize: 10 }}>Commission</Eyebrow></TableCell>
                <TableCell align="right"><Eyebrow sx={{ fontSize: 10 }}>Net payout</Eyebrow></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payouts.bookings.length === 0 ? (
                <TableRow><TableCell colSpan={5} sx={{ border: 0 }}><EmptyState Icon={Wallet} label="No bookings yet." testId="no-payouts" /></TableCell></TableRow>
              ) : payouts.bookings.map((b) => (
                <TableRow key={b.id} data-testid={`payout-row-${b.id}`} sx={{ "&:hover": { bgcolor: (t) => alpha(t.palette.text.primary, 0.02) } }}>
                  <TableCell>{b.villa_name}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{format(new Date(b.check_in), "dd MMM")} – {format(new Date(b.check_out), "dd MMM")}</TableCell>
                  <TableCell align="right">₹{b.total.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ color: "text.secondary" }}>− ₹{b.commission.toLocaleString()}</TableCell>
                  <TableCell align="right" className="text-gold" sx={{ fontWeight: 600 }}>₹{b.net_payout.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </TabPanel>
    </Box>
  );
}
